import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { useParams, useNavigate } from 'react-router-dom';
import FlightForm from './FlightForm';
import { LoadPanel } from 'devextreme-react/load-panel';

const EditFlight = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [initialSelectedIds, setInitialSelectedIds] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSelectedIds = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await api.get(`/flights/getSelectedIds/${id}`, {
          transformResponse: [
            // Custom response transformer to handle both JSON and CSV formats
            function (data) {
              if (!data.trim()) return []; // Empty response
              
              try {
                return JSON.parse(data); // Try parsing as JSON first
              } catch (e) {
                // Fallback to comma-separated values if JSON parsing fails
                return data.split(',')
                  .map(id => id.trim())
                  .filter(id => id.length > 0);
              }
            }
          ]
        });
  
        setInitialSelectedIds(response.data);
        
      } catch (error) {
        console.error('Error fetching selected IDs:', {
          message: error.message,
          status: error.response?.status,
          data: error.response?.data
        });
        
        setError(
          error.response?.data?.message || 
          error.message || 
          'Failed to load selected IDs'
        );  
      } finally {
        setLoading(false);
      }
    };
  
    if (id) {  // Only fetch if ID exists
      fetchSelectedIds();
    } else {
      setInitialSelectedIds([]); // Clear if no ID
    }
  }, [id]);

  const handleSubmit = async (formData) => {
    try {
      setLoading(true);
      setError(null);
  
      // Date preparation helper
      const prepareDate = (date) => date ? new Date(date).toISOString() : null;
  
      // Construct request payload
      const requestBody = {
        Flight: {
          ...formData,
          id: id, // Include the ID for update
          departDate: prepareDate(formData.departDate),
          arrivalDate: prepareDate(formData.arrivalDate),
        },
        SelectedIds: initialSelectedIds
      };
  
      // Make the API call
      const response = await api.put(`/flights/${id}`, requestBody);
  
      // Successful update handling
      navigate("/flights/flightlist", { 
        state: { 
          success: true,
          message: "Flight updated successfully",
          updatedData: response.data // Include any returned data
        } 
      });
  
    } catch (error) {
      console.error('Flight update failed:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data
      });
  
      // User-friendly error message with fallbacks
      const errorMessage = error.response?.data?.message 
        || error.response?.data?.title 
        || error.message 
        || 'Failed to update flight';
  
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="card p-3">
          <LoadPanel visible={true} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-4">
        <div className="card p-3">
          <div className="alert alert-danger">
            <h5>Error</h5>
            <p>{error}</p>
          </div>
          <button 
            className="btn es-button"
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <FlightForm 
      id={id} 
      onSubmit={handleSubmit} 
      selectedIds={initialSelectedIds} 
      isEditMode={true}
    />
  );
};

export default EditFlight;